import { defineStore } from 'pinia';
import { companyService } from '@/services/companyService';
import type { 
  Company, 
  CompanyQueryParams, 
  CompanySearchParams 
} from '@/types/company';
import { ElMessage } from 'element-plus';

interface CompanyState {
  companies: Company[];
  currentCompany: Company | null;
  loading: boolean;
  total: number;
  page: number;
  size: number;
}

export const useCompanyStore = defineStore('company', {
  state: (): CompanyState => ({
    companies: [],
    currentCompany: null,
    loading: false,
    total: 0,
    page: 0,
    size: 10
  }),
  
  actions: {
    // 创建公司
    async createCompany(company: Company) {
      try {
        this.loading = true;
        const response = await companyService.createCompany(company);
        if (response.code === 200) {
          ElMessage.success('公司创建成功');
          return response.data;
        } else {
          ElMessage.error(response.message || '创建失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '创建公司时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 更新公司
    async updateCompany(company: Company) {
      try {
        this.loading = true;
        const response = await companyService.updateCompany(company);
        if (response.code === 200) {
          ElMessage.success('公司更新成功');
          // 更新当前列表中的公司
          const index = this.companies.findIndex(c => c.id === company.id);
          if (index !== -1) {
            this.companies[index] = response.data;
          }
          return response.data;
        } else {
          ElMessage.error(response.message || '更新失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '更新公司时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 删除公司
    async deleteCompany(id: number) {
      try {
        this.loading = true;
        const response = await companyService.deleteCompany(id);
        if (response.code === 200) {
          ElMessage.success('公司删除成功');
          // 从列表中移除
          this.companies = this.companies.filter(c => c.id !== id);
          return true;
        } else {
          ElMessage.error(response.message || '删除失败');
          return false;
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '删除公司时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 获取单个公司详情
    async getCompany(id: number) {
      try {
        this.loading = true;
        const response = await companyService.getCompany(id);
        if (response.code === 200) {
          this.currentCompany = response.data;
          return response.data;
        } else {
          ElMessage.error(response.message || '获取公司详情失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '获取公司详情时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 条件查询公司
    async queryCompanies(params: CompanyQueryParams) {
      try {
        this.loading = true;
        const queryParams = {
          ...params,
          page: params.page !== undefined ? params.page : this.page,
          size: params.size !== undefined ? params.size : this.size
        };
        const response = await companyService.queryCompanies(queryParams);
        console.log('Query response:', response); // 添加日志
        
        // 检查响应格式
        if (!response) {
          throw new Error('No response received');
        }

        if (response.code !== 200) {
          throw new Error(response.message || '查询失败');
        }

        if (!response.data) {
          throw new Error('No data in response');
        }

        // 更新 store 状态
        this.companies = Array.isArray(response.data.content) ? response.data.content : [];
        this.total = response.data.totalElements || 0;
        this.page = response.data.number || 0;
        this.size = response.data.size || 10;

        return response.data;
      } catch (error: any) {
        console.error('查询公司列表失败:', error);
        ElMessage.error(error.message || '查询公司列表时发生错误');
        return null;
      } finally {
        this.loading = false;
      }
    },

    // 关键字搜索公司
    async searchCompanies(params: CompanySearchParams) {
      try {
        this.loading = true;
        const searchParams = {
          ...params,
          page: params.page !== undefined ? params.page : this.page,
          size: params.size !== undefined ? params.size : this.size
        };
        const response = await companyService.searchCompanies(searchParams);
        if (response.code === 200) {
          this.companies = response.data.content;
          this.total = response.data.totalElements;
          this.page = response.data.number;
          this.size = response.data.size;
          return response.data;
        } else {
          ElMessage.error(response.message || '搜索公司失败');
        }
      } catch (error: any) {
        ElMessage.error(error.response?.data?.message || '搜索公司时发生错误');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // 重置状态
    resetState() {
      this.companies = [];
      this.currentCompany = null;
      this.total = 0;
      this.page = 0;
    }
  }
}); 